import {createSlice} from "@reduxjs/toolkit";

import {
  addCondition,
  addContact,
  addContactNote,
  // addControlTerms,
  addEmailTemplate,
  addFactory,
  addFactoryNote,
  cancelFactoryCreation,
  cancelFactoryUpdate,
  createExternalAccountV2,
  deleteCondition,
  deleteContact,
  deleteContactNote,
  // deleteControlTerms,
  deleteEmailTemplate,
  deleteFactoryNote,
  fetchAllConditions,
  fetchAllContacts,
  fetchAllFactories,
  fetchContact,
  // fetchControlTerms,
  fetchFactory,
  fetchFactoryConditions,
  fetchFactoryContactConditions,
  fetchFactoryGroups,
  fetchFilters,
  fetchReferences,
  fetchSupplyCondition,
  getAllFactoriesV2,
  getContactByIdV2,
  getFactoryByIdV2,
  getFactoryContactsV2,
  getFactoryContactV2,
  getFactoryFilter,
  getFactoryFilters,
  saveFactory,
  updateCondition,
  updateContact,
  updateContactNote,
  updateEmailTemplate,
  updateExternalAccountV2,
  updateFactory,
  updateFactoryNote,
  updateFactoryV2,
  updateTermsForCondition,
} from "../operations/FactoryOperations";

export const DEFAULT_TIME_UNIT_ID = 19;

const normalizeFactoryNotes = payload => {
  const rawNotes = payload?.note ?? payload?.notes;
  if (!Array.isArray(rawNotes)) return [];

  return rawNotes
    .map((noteItem, index) => {
      if (typeof noteItem === "string") {
        return {noteId: index + 1, note: noteItem};
      }

      if (noteItem?.note) {
        return {noteId: noteItem.noteId ?? index + 1, note: noteItem.note};
      }

      return null;
    })
    .filter(Boolean);
};

const normalizeContactNotes = payload => {
  const rawNotes = payload?.note ?? payload?.notes;
  if (!Array.isArray(rawNotes)) return [];

  return rawNotes
    .map((noteItem, index) => {
      if (typeof noteItem === "string") {
        return {noteId: index + 1, note: noteItem};
      }

      if (noteItem?.note) {
        return {noteId: noteItem.noteId ?? index + 1, note: noteItem.note};
      }

      return null;
    })
    .filter(Boolean);
};

const initialState = {
  factoriesLoading: false,
  error: [],
  factories: null,
  factoryGroups: null,
  currentFactory: null,
  currentFactoryId: null,
  references: null,
  conditions: null,
  newCondition: null,
  currentFactoryConditions: null,
  factoryContactConditions: null,
  currentConditionId: null,
  contacts: null,
  newContact: null,
  currentContactId: null,
  currentContact: null,
  currentFactoryContact: null,
  factoryContacts: [],
  currentFactoryName: null,
  factoryNotes: [],
  contactNotes: [],
  refs: {
    countries: null,
    factories: null,
  },
};

export const FACTORIES_STORE_NAME = "factories";

const FactorySlice = createSlice({
  name: "factories",
  initialState,
  reducers: {
    setCurrentFactoryId: (state, {payload}) => {
      state.currentFactoryId = payload;
    },
    setCurrentContactId: (state, {payload}) => {
      state.currentContactId = payload;
      state.newContact = null;
    },
    setCurrentConditionId: (state, {payload}) => {
      state.currentConditionId = payload;
      // state.newCondition = null;
    },
    factoryCleanCurrent: state => {
      state.currentFactory = null;
      state.newContact = null;
      state.currentConditionId = null;
      state.currentContactId = null;
      state.newCondition = null;
      state.newContact = null;
      state.currentFactoryConditions = null;
      state.factoryContactConditions = null;
      state.currentTimeUnitId = DEFAULT_TIME_UNIT_ID;
      state.factoryNotes = null;
    },
    cleanFactoryContactConditions: state => {
      state.factoryContactConditions = null;
    },
    cleanCurrentFactoryConditions: state => {
      state.currentFactoryConditions = null;
    },
    setCurrentFactoryName: (state, {payload}) => {
      state.currentFactoryName = payload || null;
    },
    setEmailTemplates: (state, {payload}) => {
      state.emailTemplates = payload || null;
    },
    cleanControlTerms: state => {
      state.controlTerms = null;
    },
    clearCurrentFactory: state => {
      state.currentFactory = null;
    },
    clearCurrentFactoryContact: state => {
      state.currentFactoryContact = null;
    },
    clearFactoryContacts: state => {
      state.factoryContacts = [];
    },
    // setCurrentTimeUnitId: (state, {payload}) => {
    //   state.currentTimeUnitId = payload;
    // },
  },
  extraReducers: builder => {
    builder
      //*** FACTORIES ***
      //for get requests
      .addCase(fetchAllFactories.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.factories = payload.currentPage > 1 ? [...state.factories, ...payload.data] : payload.data;
      })
      .addCase(fetchFactoryGroups.fulfilled, (state, {payload}) => {
        state.factoryGroups = payload;
      })
      .addCase(fetchFactory.fulfilled, (state, {payload}) => {
        state.currentFactory = payload;
        state.factoryNotes = normalizeFactoryNotes(payload);
      })
      .addCase(fetchReferences.fulfilled, (state, {payload}) => {
        state.references = payload;
      })
      .addCase(getFactoryFilter.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.refs.countries = payload.countries;
        state.refs.factories = payload.factories;
      })
      //for post requests
      .addCase(addFactory.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentFactory = payload;
        state.currentFactoryId = payload.factoryId;
      })
      //for patch requests
      .addCase(saveFactory.fulfilled, (state, {payload}) => {
        state.conditions = [payload, ...(state.conditions ?? [])];
        // state.currentFactoryId = null;
        state.currentContactId = null;
        state.currentFactory = null;
        state.newContact = null;
        state.currentConditionId = null;
        state.newCondition = null;
        state.newContact = null;
        state.currentFactoryConditions = null;
        state.factoryContactConditions = null;
        state.currentTimeUnitId = DEFAULT_TIME_UNIT_ID;
      })
      .addCase(updateFactory.fulfilled, (state, {payload}) => {
        if (payload) {
          state.currentFactory = {...state.currentFactory, ...payload};
          state.factoryNotes = normalizeFactoryNotes(state.currentFactory);
          state.conditions = state.conditions.map(condition => {
            if (condition.factory.factoryId === payload.factoryId) {
              const {factoryId, address, location, webSite, blackList} = payload;
              condition.factory = {...condition.factory, factoryId, address, location, webSite, blackList};
            }
            return condition;
          });
          state.currentTimeUnitId = DEFAULT_TIME_UNIT_ID;
        }
      })
      .addCase(addFactoryNote.fulfilled, (state, {payload}) => {
        if (state.factoryNotes?.length > 0) {
          state.factoryNotes.unshift(payload);
        } else {
          state.factoryNotes = [payload];
        }
      })
      .addCase(updateFactoryNote.fulfilled, (state, {payload}) => {
        if (!payload) return;
        const index = state.factoryNotes.findIndex(note => note.noteId === payload.noteId);
        if (index !== -1) {
          state.factoryNotes[index] = payload;
        }
      })
      .addCase(deleteFactoryNote.fulfilled, (state, {payload}) => {
        state.factoryNotes = state.factoryNotes.filter(({noteId}) => noteId !== payload);
      })
      //for delete requests
      .addCase(cancelFactoryCreation.fulfilled, state => {
        state.currentFactory = null;
        state.newContact = null;
        state.newCondition = null;
        state.currentConditionId = null;
        state.currentContactId = null;
        state.currentFactoryConditions = null;
        state.factoryContactConditions = null;
        state.currentFactoryId = null;
        state.currentTimeUnitId = DEFAULT_TIME_UNIT_ID;
      })
      .addCase(cancelFactoryUpdate.fulfilled, state => {
        state.currentFactory = null;
        state.currentContactId = null;
        state.currentConditionId = null;
        state.currentFactoryConditions = null;
        state.factoryContactConditions = null;
        state.newCondition = null;
        state.currentTimeUnitId = DEFAULT_TIME_UNIT_ID;
      })

      //*Factories V2
      .addCase(getAllFactoriesV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.factories = payload.currentPage > 1 ? [...state.factories, ...payload.data] : payload.data;
      })
      .addCase(getFactoryByIdV2.fulfilled, (state, {payload}) => {
        state.currentFactory = payload;
      })
      .addCase(getFactoryFilters.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.refs.countries = payload.countries;
        state.refs.factories = payload.factories;
      })
      .addCase(updateFactoryV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentFactory = {...state.currentFactory, ...payload};
        const index = state.factories.findIndex(factory => factory.factoryId === payload.factoryId);
        if (index > 0) state.factories.splice(index, 1, payload);
      })
      .addCase(getContactByIdV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentContact = payload;
      })
      .addCase(createExternalAccountV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        if (payload.ownerType === "factory") {
          state.currentFactory.externalAccounts.unshift(payload);
          state.factories.find(factory => factory.factoryId === payload.factoryId)?.externalAccounts.unshift(payload);
        }
        if (payload.ownerType === "contact") {
          state.currentFactoryContact.contact.externalAccounts.unshift(payload);
        }
      })
      .addCase(updateExternalAccountV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        if (payload.ownerType === "factory") {
          state.currentFactory.externalAccounts[0] = payload;
          const index = state.factories.findIndex(factory => factory.factoryId === payload.factoryId);
          if (index !== -1) state.factories[index].externalAccounts[0] = payload;
        }
        if (payload.ownerType === "contact") {
          state.currentFactoryContact.contact.externalAccounts[0] = payload;
        }
      })
      .addCase(getFactoryContactV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentFactoryContact = payload;
      })
      .addCase(getFactoryContactsV2.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.factoryContacts = payload;
      })

      //*** SUPPLY CONDITIONS ***
      //for get requests
      .addCase(fetchAllConditions.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.conditions = payload.currentPage > 1 ? [...state.conditions, ...payload.data] : payload.data;
      })
      .addCase(fetchFactoryContactConditions.fulfilled, (state, {payload}) => {
        if (payload) {
          state.factoryContactConditions = payload.data;
        }
      })
      .addCase(fetchFactoryConditions.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentFactoryConditions =
          payload.currentPage > 1 ? [...state.currentFactoryConditions, ...payload.data] : payload.data;
      })
      .addCase(fetchFilters.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.refs.countries = payload.countries;
        state.refs.factories = payload.factories;
      })
      .addCase(fetchSupplyCondition.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentConditionId = payload.conditionId;
        // state.currentFactoryConditions
      })
      //for post requests
      .addCase(addCondition.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.newCondition = payload;
        state.currentConditionId = payload.conditionId;
        state.factoryContactConditions = [...(state.factoryContactConditions ?? []), payload];
        state.currentFactory.supplyConditions = [...(state.currentFactory.supplyConditions ?? []), payload];
      })
      //for patch requests
      .addCase(updateCondition.fulfilled, (state, {payload}) => {
        if (Array.isArray(state.factoryContactConditions)) {
          state.factoryContactConditions = state.factoryContactConditions.map(condition => {
            if (condition.conditionId === state.currentConditionId) {
              condition = payload ?? condition;
            }
            return condition;
          });
        }
        if (Array.isArray(state.currentFactoryConditions)) {
          state.currentFactoryConditions = state.currentFactoryConditions.map(condition => {
            if (condition.conditionId === state.currentConditionId) {
              condition = payload ?? condition;
            }
            return condition;
          });
        }
      })
      //for delete requests
      .addCase(deleteCondition.fulfilled, (state, {meta}) => {
        state.currentConditionId = null;
        const conditionId = meta.arg;
        state.conditions = state.conditions.filter(condition => condition.conditionId !== conditionId);
        state.factoryContactConditions = state.factoryContactConditions.filter(
          condition => condition.conditionId !== conditionId,
        );
      })

      //*** REPRESENTATIVE CONTACTS ***
      //for get requests
      .addCase(fetchAllContacts.fulfilled, (state, {payload}) => {
        state.contacts = payload;
      })
      .addCase(fetchContact.fulfilled, (state, {payload}) => {
        if (!payload) return;
        if (state.currentFactory?.contacts && Array.isArray(state.currentFactory.contacts)) {
          state.currentFactory.contacts = state.currentFactory.contacts.map(contact =>
            contact.contactId === payload.contactId ? {...contact, ...payload} : contact,
          );
        }

        if (payload?.contactId === state.currentContactId) {
          state.newContact = payload;
          state.contactNotes = normalizeContactNotes(payload);
        }
      })
      //for post requests
      .addCase(addContact.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentFactory.contacts = [...(state.currentFactory.contacts ?? []), payload];
        state.newContact = payload;
        state.currentContactId = payload?.contactId;
        state.contactNotes = normalizeContactNotes(payload);
      })
      .addCase(addContactNote.fulfilled, (state, {payload}) => {
        if (state.contactNotes?.length > 0) {
          state.contactNotes.unshift(payload);
        } else {
          state.contactNotes = [payload];
        }
      })
      //for patch requests
      .addCase(updateContact.fulfilled, (state, {payload}) => {
        if (!payload) return;
        if (Array.isArray(state.currentFactory?.contacts)) {
          state.currentFactory.contacts = state.currentFactory.contacts.map(contact =>
            contact.contactId === payload.contactId ? payload : contact,
          );
        }

        if (payload?.contactId === state.currentContactId) {
          state.newContact = payload;
          state.contactNotes = normalizeContactNotes(payload);
        }

        if (Array.isArray(state.conditions)) {
          state.conditions = state.conditions.map(condition => {
            if (condition.contactId === payload.contactId) {
              const {contactId, email, name, phone, blackList} = payload;
              condition.factory = {...condition.factory, blackList};
              condition.representativeContact = {...condition.representativeContact, contactId, email, name, phone};
            }
            return condition;
          });
        }

        if (Array.isArray(state.currentFactoryConditions)) {
          state.currentFactoryConditions = state.currentFactoryConditions.map(condition => {
            if (condition.contactId === payload.contactId) {
              condition.factory = {...condition.factory, ...payload?.blackList};
              condition.representativeContact = {...condition.representativeContact, ...payload};
            }
            return condition;
          });
        }
      })
      .addCase(updateContactNote.fulfilled, (state, {payload}) => {
        if (!payload) return;
        const index = state.contactNotes.findIndex(note => note.noteId === payload.noteId);
        if (index !== -1) {
          state.contactNotes[index] = payload;
        }
      })
      //for delete requests
      .addCase(deleteContact.fulfilled, (state, {meta}) => {
        state.currentContactId = null;
        const contactId = meta.arg;
        state.newContact = null;
        state.contacts = state.contacts.filter(contact => contact.contactId !== contactId);
        state.currentFactory.contacts = state.currentFactory.contacts.filter(
          contact => contact.contactId !== contactId,
        );
      })
      .addCase(deleteContactNote.fulfilled, (state, {payload}) => {
        state.contactNotes = state.contactNotes.filter(note => note.noteId !== payload);
      })

      //*** EMAIL TEMPLATES ***
      // .addCase(fetchEmailTemplates.fulfilled, (state, {payload}) => {
      // state.emailTemplates = payload;
      // })
      .addCase(addEmailTemplate.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentFactoryConditions = state.currentFactoryConditions.map(condition => {
          if (condition.conditionId === payload.conditionId) {
            condition.requestEmailTemplate = payload;
          }
          return condition;
        });
      })
      .addCase(updateEmailTemplate.fulfilled, (state, {payload}) => {
        if (!payload) return;
        state.currentFactoryConditions = state.currentFactoryConditions.map(condition => {
          if (condition.conditionId === payload.conditionId) {
            condition.requestEmailTemplate = payload;
          }
          return condition;
        });
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, {meta}) => {
        state.currentFactoryConditions = state.currentFactoryConditions.map(condition => {
          if (condition.requestEmailTemplate?.templateId === meta.arg) {
            condition.requestEmailTemplate = null;
          }
          return condition;
        });
      })

      //*** CONTROL TERMS ***
      // .addCase(fetchControlTerms.fulfilled, (state, {payload}) => {
      //   state.controlTerms = payload;
      // })
      // .addCase(addControlTerms.fulfilled, (state, {payload}) => {
      //   state.controlTerms = payload;
      // })
      .addCase(updateTermsForCondition.fulfilled, (state, {payload, meta}) => {
        if (!payload) return;
        state.currentFactoryConditions = state.currentFactoryConditions.map(condition => {
          if (condition.conditionId === meta.arg.conditionId) {
            condition.controlTerms = [...payload].sort((a, b) => a.termId - b.termId);
          }
          return condition;
        });
      })
      // .addCase(deleteControlTerms.fulfilled, state => {
      //   state.controlTerms = [];
      // })

      //matchers
      .addMatcher(
        action => action.type.startsWith(FACTORIES_STORE_NAME) && action.type.endsWith("/fulfilled"),
        state => handleFulfilled(state),
      )
      .addMatcher(
        action => action.type.startsWith(FACTORIES_STORE_NAME) && action.type.endsWith("/pending"),
        state => handlePending(state),
      )
      .addMatcher(
        action => action.type.startsWith(FACTORIES_STORE_NAME) && action.type.endsWith("/rejected"),
        state => handleRejected(state),
      );
  },
});

//matcher functions
function handleFulfilled(state) {
  state.factoriesLoading = false;
  state.error = [];
}
function handlePending(state) {
  state.factoriesLoading = true;
  state.error = [];
}
function handleRejected(state, error) {
  state.factoriesLoading = false;
  state.error = error;
}

//actions, reducer
const {actions, reducer} = FactorySlice;
export const {
  setCurrentFactoryId,
  setCurrentContactId,
  setCurrentConditionId,
  factoryCleanCurrent,
  setCurrentFactoryContacts,
  cleanFactoryContactConditions,
  setCurrentFactoryName,
  setEmailTemplates,
  cleanControlTerms,
  cleanCurrentFactoryConditions,
  clearCurrentFactory,
  clearCurrentFactoryContact,
  clearFactoryContacts,
} = actions;
export default reducer;
